type MutationMethod = "POST" | "PATCH" | "PUT" | "DELETE";

type ExtendedRequestInit = RequestInit & {
  onErrorMessage?: string | ((res: Response) => string | Promise<string>);
  /** Set to true when the endpoint may return 204 No Content. Widens the return type to `T | null`. */
  expects204?: boolean;
  /** Set to true when the endpoint returns a plain-text body instead of JSON. */
  expectsText?: boolean;
};

/** Error thrown by apiClient/apiMutate with the HTTP status code attached. */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** Type guard for narrowing caught errors to ApiError. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Extract a human-readable message from an ApiError.
 * The raw `message` is JSON.stringify'd from the API response body, so we attempt
 * to parse it and pull out a `.message` field if present.
 */
export function getApiErrorMessage(error: ApiError): string {
  try {
    const parsed = JSON.parse(error.message);
    if (parsed && typeof parsed.message === "string") {
      return parsed.message;
    }
  } catch {
    // not JSON — use as-is
  }
  return error.message;
}

const defaultErrorMessage = async (res: Response) => {
  let errorMessage = "";
  try {
    const resp = await res.json();
    errorMessage = JSON.stringify(resp);
  } catch {
    const text = await res.text().catch(() => "");
    errorMessage = text || `Request failed with status ${res.status}`;
  }
  return errorMessage;
};

export function apiClient<T = unknown>(url: string, options?: ExtendedRequestInit): Promise<T>;
/** Mutation overload — returns `T | null` when the endpoint may return 204 No Content. */
export function apiClient<T = unknown>(
  url: string,
  options: ExtendedRequestInit & { method: MutationMethod; expects204: true }
): Promise<T | null>;
/** Mutation overload — returns `T` (most mutations return a response body). */
export function apiClient<T = unknown>(
  url: string,
  options: ExtendedRequestInit & { method: MutationMethod }
): Promise<T>;

/**
 * Shared API client for making fetch requests with consistent error handling.
 *
 * Features:
 * - Handles cross-origin and same-origin redirects
 * - Parses JSON error responses (with fallback for non-JSON error bodies like HTML 504s)
 * - Handles 204 No Content
 * - Throws errors with `.status` for use with the global error handler
 */
export async function apiClient<T = unknown>(
  url: string,
  options: ExtendedRequestInit = {}
): Promise<T | null> {
  const { onErrorMessage, expects204, expectsText, ...fetchOptions } = options;
  const res = await fetch(url, {
    ...fetchOptions,
    redirect: "manual",
  });

  // handle cross origin redirects
  if (res.type === "opaqueredirect") {
    const parsedUrl = new URL(res.url);
    const strippedUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}${
      parsedUrl.port ? `:${parsedUrl.port}` : ""
    }`;
    window.location.href = strippedUrl;
    // Page is being redirected anyway, so return a promise that never resolves since no value should ever be consumed anyway.
    return new Promise<T>(() => {});
  }

  // handle same origin redirects by following them
  if ([301, 302, 307, 308].includes(res.status)) {
    const redirected = await fetch(url, { ...fetchOptions, redirect: "follow" });
    if (!redirected.ok) {
      let message: string;
      try {
        const resp = await redirected.json();
        message = JSON.stringify(resp);
      } catch {
        const text = await redirected.text().catch(() => "");
        message = text || `Request failed with status ${redirected.status}`;
      }
      throw new ApiError(message, redirected.status);
    }
    return expectsText ? ((await redirected.text()) as unknown as T) : redirected.json();
  }

  if (!res.ok) {
    let message: string | undefined;
    try {
      message =
        typeof onErrorMessage === "string" ? onErrorMessage : await onErrorMessage?.(res.clone());
    } catch {
      // onErrorMessage failed, fall through to default
    }
    if (!message) {
      message = await defaultErrorMessage(res);
    }
    throw new ApiError(message, res.status);
  }

  if (expects204 && res.status === 204) {
    return null;
  } else if (expects204) {
    throw new ApiError("Expected 204 No Content response", res.status);
  } else if (res.status === 204) {
    throw new ApiError("Unexpected 204 No Content response", res.status);
  }

  if (expectsText) {
    return (await res.text()) as unknown as T;
  }

  return res.json();
}

/**
 * Convenience wrapper for mutation requests (POST/PATCH/PUT/DELETE).
 * Automatically sets Content-Type and JSON-serializes the body.
 *
 * Use this for mutations in event handlers or standalone API modules where
 * React hooks can't be used. For declarative data fetching in components,
 * prefer `useFetcher` or `useMutator` from ~/hooks/useFetcher.
 *
 * Pass `{ expects204: true }` in options when the endpoint may return 204 No Content
 * to widen the return type to `T | null`.
 */
/** Overload: returns `T | null` when the endpoint may return 204 No Content. */
export function apiMutate<T = unknown>(
  url: string,
  method: MutationMethod,
  body: unknown,
  options: Omit<ExtendedRequestInit, "method" | "body"> & { expects204: true }
): Promise<T | null>;
/** Mutation — returns `T` (most mutations return a response body). */
export function apiMutate<T = unknown>(
  url: string,
  method: MutationMethod,
  body?: unknown,
  options?: Omit<ExtendedRequestInit, "method" | "body">
): Promise<T>;
export async function apiMutate<T = unknown>(
  url: string,
  method: MutationMethod,
  body?: unknown,
  options?: Omit<ExtendedRequestInit, "method" | "body">
): Promise<T | null> {
  const init: ExtendedRequestInit = {
    method,
    ...options,
  };

  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    init.headers = {
      "Content-Type": "application/json",
      ...(options?.headers || {}),
    };
    init.body = JSON.stringify(body);
  }

  return apiClient<T>(url, init);
}

/**
 * Creates a bound pair of apiClient/apiMutate with shared default options.
 * Per-call options are merged on top, so callers can still override.
 */
export const createApiClient = (defaults: Pick<ExtendedRequestInit, "onErrorMessage">) => ({
  apiClient: <T = unknown>(url: string, options?: ExtendedRequestInit) =>
    apiClient<T>(url, { ...defaults, ...options }),
  apiMutate: <T = unknown>(
    url: string,
    method: "POST" | "PATCH" | "PUT" | "DELETE",
    body?: unknown,
    options?: Omit<ExtendedRequestInit, "method" | "body">
  ) => apiMutate<T>(url, method, body, { ...defaults, ...options }),
});
